import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  LayoutDashboard,
  Users,
  Settings,
  CreditCard,
  Workflow,
  BarChart3,
  UserCheck,
  Shield,
  ShieldCheck,
  LogOut,
  Bot,
  Wallet,
  Package,
  Layers
} from 'lucide-react';

const SidebarContainer = styled.aside`
  width: 250px;
  background: ${props => props.theme.colors.surface};
  border-right: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.lg} 0;
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  left: 0;
  top: 0;
  z-index: 50;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const SidebarHeader = styled.div`
  padding: 0 ${props => props.theme.spacing.lg} ${props => props.theme.spacing.lg};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Logo = styled(Link)`
  font-size: 1.25rem;
  font-weight: bold;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const LogoIcon = styled.div`
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  border-radius: ${props => props.theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
`;

const NavSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${props => props.theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0 ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const NavItem = styled.li`
  margin: 0;
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
  
  &:hover {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primary};
  }
  
  &.active {
    background: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.primary};
    border-left-color: ${props => props.theme.colors.primary};
  }
`;

const IconWrapper = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const FooterLink = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} 0;
  color: ${props => props.theme.colors.textSecondary};
  text-decoration: none;
  font-size: 0.875rem;
  transition: color 0.2s ease;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  
  &:hover {
    color: ${props => props.theme.colors.error};
  }
`;

function Sidebar({ onLogout }) {
  const location = useLocation();

  const mainNavItems = [
    { path: '/', label: 'Дашборд', icon: LayoutDashboard },
    { path: '/users', label: 'Пользователи', icon: Users },
    { path: '/providers', label: 'Провайдеры', icon: Settings },
    { path: '/bot-actions', label: 'Действия бота', icon: Bot },
    { path: '/jobs', label: 'Задачи', icon: Workflow },
  ];

  const billingNavItems = [
    { path: '/billing', label: 'Обзор биллинга', icon: CreditCard },
    { path: '/billing/balances', label: 'Балансы', icon: Wallet },
    { path: '/billing/plans', label: 'Тарифы', icon: Layers },
    { path: '/billing/packages', label: 'Пакеты', icon: Package },
    { path: '/payments', label: 'Платежи', icon: CreditCard },
    { path: '/billing/subscriptions', label: 'Подписки', icon: Shield },
  ];

  const analyticsNavItems = [
    { path: '/referrals', label: 'Рефералы', icon: UserCheck },
    { path: '/analytics', label: 'Аналитика', icon: BarChart3 }
  ];

  const systemNavItems = [
    { path: '/admin-users', label: 'Администраторы', icon: ShieldCheck },
    { path: '/settings', label: 'Настройки', icon: Shield }
  ];

  return (
    <SidebarContainer>
      <SidebarHeader>
        <Logo to="/">
          <LogoIcon>K</LogoIcon>
          KES.AI Admin
        </Logo>
      </SidebarHeader>

      <NavSection>
        <SectionTitle>Основное</SectionTitle>
        <NavList>
          {mainNavItems.map(item => (
            <NavItem key={item.path}>
              <NavLink
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                <IconWrapper>
                  <item.icon size={20} />
                </IconWrapper>
                {item.label}
              </NavLink>
            </NavItem>
          ))}
        </NavList>
      </NavSection>

      <NavSection>
        <SectionTitle>Биллинг</SectionTitle>
        <NavList>
          {billingNavItems.map(item => (
            <NavItem key={item.path}>
              <NavLink
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                <IconWrapper>
                  <item.icon size={20} />
                </IconWrapper>
                {item.label}
              </NavLink>
            </NavItem>
          ))}
        </NavList>
      </NavSection>

      <NavSection>
        <SectionTitle>Аналитика</SectionTitle>
        <NavList>
          {analyticsNavItems.map(item => (
            <NavItem key={item.path}>
              <NavLink
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                <IconWrapper>
                  <item.icon size={20} />
                </IconWrapper>
                {item.label}
              </NavLink>
            </NavItem>
          ))}
        </NavList>
      </NavSection>

      <NavSection>
        <SectionTitle>Система</SectionTitle>
        <NavList>
          {systemNavItems.map(item => (
            <NavItem key={item.path}>
              <NavLink
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
              >
                <IconWrapper>
                  <item.icon size={20} />
                </IconWrapper>
                {item.label}
              </NavLink>
            </NavItem>
          ))}
        </NavList>
      </NavSection>

      <SidebarFooter>
        <FooterLink onClick={onLogout}>
          <LogOut size={16} />
          Выйти
        </FooterLink>
      </SidebarFooter>
    </SidebarContainer>
  );
}

export default Sidebar;
